import {useState} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {DescargarEvidencia} from '@/components/descargar-evidencia'
import {Tag} from '@/components/ui/tag'
import {Skeleton} from '@/components/ui/skeleton'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {PorQue} from '@/components/ui/por-que'
import {CertBlockedBanner} from '@/components/sgsst/cert-blocked-banner'
import {MicroLabel} from '@/components/period-selector'
import {QueEsUnaVersionCertificada, VersionCongeladaNota} from '@/components/certificacion-explicacion'
import {
  useCertificacion,
  useCertificar,
  type CertificacionArtefacto,
  type EstandarRespaldado,
} from '@/hooks/sgsst/expediente/use-certificacion'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {rutaDeDescarga, useEvidence} from '@/hooks/sgsst/evidencias/use-evidence'
import {useSgsst} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {formatDate} from '@/lib/risk'
import {translateApiError} from '@/lib/errors'

// 2.7.1 is auto-credited by art. 27 in Capítulo I (chapter flags false in sgsst_standards.csv), so
// certifying stamps nothing there. Advisory only; post-certify truth is `vigente.estandares`.
const NEVER_APPLIES_IN_CHAPTER_1: Partial<Record<CertificacionArtefacto, boolean>> = {
  matriz_legal: true,
  // 6.1.1 comparte chapter_1/chapter_2=false: auto-acreditado por art. 27 en Cap. I.
  indicadores: true,
}

// Amber inside 60 days (the certification's cadence is longer than a soporte's), red once past.
function vigenciaTone(venceEl: string, today = new Date()): 'green' | 'amber' | 'red' {
  const dueDate = new Date(`${venceEl}T00:00:00`)
  const diffDays = Math.floor((dueDate.getTime() - today.getTime()) / 86_400_000)
  if (diffDays < 0) return 'red'
  if (diffDays < 60) return 'amber'
  return 'green'
}

/** Kairos propone y el humano dispone: el botón lleva al checklist y nunca marca desde aquí,
 * porque la marca es un acto con consecuencia legal. */
function RespaldaBlock({businessId, respalda}: {businessId: string; respalda: EstandarRespaldado[]}) {
  const {t} = useTranslation()
  if (respalda.length === 0) return null
  return (
    <div className="rounded-lg bg-secondary/50 p-3" data-testid="certificacion-respalda">
      <span className="font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
        {t('certificacion.respalda.titulo')}
      </span>
      <p className="mt-1 text-xs text-muted-foreground">{t('certificacion.respalda.aviso')}</p>
      <ul className="mt-2 divide-y divide-border/60">
        {respalda.map((estandar) => (
          <li key={estandar.code} className="flex flex-wrap items-center justify-between gap-2 py-2">
            <div className="min-w-0 flex-1">
              <span
                className="font-mono text-xs font-medium text-muted-foreground"
                // Solo los estándares que el acto estampa: un auto-acreditado por art. 27 nunca lleva chip.
                data-testid={estandar.aplica ? 'certificacion-estandar-chip' : undefined}>
                {estandar.code}
              </span>
              <span className="ml-2 text-sm text-foreground">{estandar.name}</span>
            </div>
            {!estandar.aplica ? (
              <Tag tone="neutral" data-testid={`certificacion-auto-acreditado-${estandar.code}`}>
                {t('certificacion.respalda.autoAcreditado')}
              </Tag>
            ) : estandar.status === 'cumple' ? (
              <Tag tone="green" data-testid={`certificacion-ya-cumple-${estandar.code}`}>
                {t('certificacion.respalda.yaCumple')}
              </Tag>
            ) : (
              <a
                href={`/businesses/${businessId}/sgsst#std-${estandar.code}`}
                className="text-xs font-medium whitespace-nowrap text-primary hover:underline"
                data-testid={`certificacion-usar-para-cumplir-${estandar.code}`}>
                {t('certificacion.respalda.usarParaCumplir')}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** W-CERT2 Frente A. `huecosExamenes` only comes from `examenes`, for the confirm copy. */
export function CertificacionTab({
  businessId,
  artefacto,
  huecosExamenes,
}: {
  businessId: string
  artefacto: CertificacionArtefacto
  huecosExamenes?: number
}) {
  const {t} = useTranslation()
  const {data, isLoading} = useCertificacion(businessId, artefacto)
  const {data: evidenceYear} = useEvidence(businessId)
  const {data: sgsstState} = useSgsst(businessId)
  const capabilities = useCapabilities(businessId)
  const godReadOnly = useGodModeReadOnly()
  const certificar = useCertificar(businessId, artefacto)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (isLoading || !data) {
    return <Skeleton className="h-40 w-full" data-testid="certificacion-tab-loading" />
  }

  const {vigente, deriva} = data
  // Certificar es un acto de diseño (arts. 4/10/17): guarda el `credential_snapshot` de la licencia.
  const canCertify = capabilities.sgsst_disenar && !godReadOnly
  const blockedReason = capabilities.disenar_bloqueado_por
  const documento = vigente ? evidenceYear?.items.find((item) => item.id === vigente.evidencia_id) : undefined

  function abrirConfirmacion() {
    setError(null)
    setConfirming(true)
  }

  function confirmar() {
    certificar.mutate(undefined, {
      onSuccess: () => setConfirming(false),
      onError: (err) => {
        setError(translateApiError(err))
        setConfirming(false)
      },
    })
  }

  const nextVersion = (vigente?.version ?? 0) + 1
  const estandaresPreview = vigente?.estandares ?? []
  // Definitive once certified (empty estandares); advisory before, off the pinned chapter.
  const chapter = sgsstState?.assessment?.chapter
  const neverApplies =
    (vigente != null && estandaresPreview.length === 0) ||
    (vigente == null && chapter === 1 && !!NEVER_APPLIES_IN_CHAPTER_1[artefacto])

  return (
    <div className="space-y-4" data-testid="certificacion-tab">
      {!canCertify && blockedReason && <CertBlockedBanner reason={blockedReason} businessId={businessId} />}

      {vigente ? (
        <div className="surface overflow-hidden" data-testid="certificacion-vigente">
          <div className="surface-divider flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
            <div className="flex items-center gap-2">
              {/* Un número de versión es identidad, no «al día» (U2(a)), igual que en
                  `pages/certificacion-contenido.tsx`. */}
              <Tag tone="neutral" data-testid="certificacion-version">
                {t('certificacion.version', {n: vigente.version})}
              </Tag>
              <Tag tone={vigenciaTone(vigente.vence_el)} data-testid="certificacion-vigencia">
                {t('certificacion.vigenciaHasta', {date: formatDate(vigente.vence_el)})}
              </Tag>
            </div>
            {documento && (
              <DescargarEvidencia
                ruta={rutaDeDescarga(businessId, documento.id)}
                className="text-sm font-medium text-primary hover:underline"
                testId="certificacion-descargar">
                {t('certificacion.descargar', {filename: documento.filename})}
              </DescargarEvidencia>
            )}
          </div>
          <div className="space-y-3 px-4 py-3.5">
            <p className="text-sm text-muted-foreground" data-testid="certificacion-firma">
              {t('certificacion.certificadoPor', {
                nombre: vigente.certificado_por,
                fecha: formatDate(vigente.certificado_en),
              })}
            </p>
            <VersionCongeladaNota artefacto={artefacto} />
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={`/businesses/${businessId}/sgsst/certificaciones/${artefacto}`}
                className="text-sm font-medium text-primary hover:underline"
                data-testid="certificacion-ver-contenido">
                {t(`certificacion.verContenido.${artefacto}`)}
              </Link>
            </div>

            {estandaresPreview.length === 0 && (
              <p className="text-xs text-muted-foreground" data-testid="certificacion-sin-estandares">
                {t('certificacion.sinEstandaresCapitulo', {
                  chapter: chapter ?? '—',
                })}
              </p>
            )}

            <RespaldaBlock businessId={businessId} respalda={vigente.respalda} />

            <p className="text-xs text-muted-foreground" data-testid="certificacion-en-el-zip">
              {t('certificacion.entraAlZip')}
            </p>

            {/* U19: no es chip porque no recorta ninguna lista; es un enlace a la versión congelada. */}
            {deriva && deriva.cambios > 0 && (
              <div className="notice-amber space-y-1.5 rounded-lg px-4 py-3" data-testid="certificacion-deriva-banner">
                <p className="flex flex-wrap items-center gap-2 text-sm">
                  <Link
                    to={`/businesses/${businessId}/sgsst/certificaciones/${artefacto}`}
                    className="font-medium text-primary hover:underline"
                    data-testid="certificacion-deriva-enlace">
                    {t('certificacion.deriva.aviso', {count: deriva.cambios})}
                  </Link>
                  <PorQue
                    label={t('certificacion.deriva.porqueLabel')}
                    testId="certificacion-deriva-porque"
                    contenidoTestId="certificacion-deriva-porque-contenido">
                    {t('certificacion.deriva.porqueBody')}
                    <ul className="mt-2 ml-4 list-disc text-xs text-muted-foreground">
                      {deriva.detalle.map((linea, i) => (
                        <li key={i}>{linea}</li>
                      ))}
                    </ul>
                  </PorQue>
                </p>
              </div>
            )}
          </div>

          {canCertify && (
            <div className="surface-divider border-t bg-secondary/40 px-4 py-3">
              <Button size="sm" onClick={abrirConfirmacion} data-testid="certificar-de-nuevo">
                {t('certificacion.certificarDeNuevo')}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div
          className="surface surface-raised mx-auto max-w-2xl space-y-5 p-6 sm:p-8"
          data-testid="certificacion-empty">
          <div className="space-y-1">
            <MicroLabel>{t('certificacion.queEs.eyebrow')}</MicroLabel>
            <h2 className="font-heading text-lg text-foreground">{t(`certificacion.empty.headline.${artefacto}`)}</h2>
          </div>
          <QueEsUnaVersionCertificada artefacto={artefacto} />
          {neverApplies && (
            <p className="notice-amber rounded-lg px-4 py-3 text-sm" data-testid="certificacion-sin-estandares">
              {t('certificacion.sinEstandaresCapitulo', {chapter})}
            </p>
          )}
          {canCertify && (
            <div className="border-t border-border pt-4">
              <Button onClick={abrirConfirmacion} data-testid="certificar-btn">
                {t('certificacion.certificar')}
              </Button>
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="text-sm text-destructive" data-testid="certificacion-error">
          {error}
        </p>
      )}

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t('certificacion.confirmar.title', {n: nextVersion})}
        description={
          neverApplies
            ? t('certificacion.confirmar.bodySinEstandares', {n: nextVersion, chapter: chapter ?? '—'})
            : huecosExamenes
              ? t('certificacion.confirmar.bodyConHuecos', {n: nextVersion, huecos: huecosExamenes})
              : t('certificacion.confirmar.body', {n: nextVersion})
        }
        confirmLabel={t('certificacion.confirmar.confirm')}
        cancelLabel={t('common.cancel')}
        pending={certificar.isPending}
        onConfirm={confirmar}
      />
    </div>
  )
}
